import { ArrowLeftIcon, ChevronDown } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@r/components/ui/collapsible.tsx';
import { cn } from '@r/utils/utils.ts';
import { useProductFilters } from '@r/hooks/use-product-filters.ts';
import { useShopBrands } from '@r/hooks/use-shop-brands.ts';
import { getProductCategoriesToFetch } from '@r/helpers/product-catalog.ts';
import { ProductBrandFilters } from '@client/components/shop/product-brand-filters.tsx';
import { useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import type {
  ShopShopReadModel,
  ProductCategoryProductCategoryReadModel
} from '@r/api/generated/client.schemas.ts';

type Shop = ShopShopReadModel;
type ProductCategory = ProductCategoryProductCategoryReadModel;

type ShopProductsSidebarProps = {
  shop: Shop;
  productCategories: ProductCategory[];
};

type SearchParams = {
  subcategoryId?: string | number;
  categoryId?: string | number;
  [key: string]: unknown;
};

const ShopProductsSidebar = ({ shop, productCategories }: ShopProductsSidebarProps) => {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as SearchParams;

  const subcategoryId = search.subcategoryId ? String(search.subcategoryId) : undefined;
  const categoryId = search.categoryId ? String(search.categoryId) : undefined;
  const categoriesToFetch = useMemo(
    () => getProductCategoriesToFetch(subcategoryId, categoryId, productCategories),
    [subcategoryId, categoryId, productCategories]
  );

  const { data: brands = [], isSuccess: isBrandsSuccess } = useShopBrands({
    shopId: String(shop.id),
    categoryIds: categoriesToFetch
  });
  const availableBrandIds = useMemo(() => brands.map((brand) => brand.id), [brands]);

  const {
    searchInput,
    setSearchInput,
    selectedCategoryId,
    selectedSubCategoryId,
    selectedBrandIds,
    openCategoryIds,
    setCategoryOpen,
    handleSelectCategory,
    handleSelectSubCategory,
    handleResetCategories,
    handleToggleBrand
  } = useProductFilters({
    productCategories,
    availableBrandIds: isBrandsSuccess ? availableBrandIds : undefined
  });

  return (
    <Card className="hidden md:block md:sticky left-0 top-5 z-10 border-none h-fit w-full md:w-[260px] lg:w-[300px] flex-shrink-0">
      <CardHeader className="flex flex-row flex-nowrap items-start space-x-2.5 space-y-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/shops"
                className="p-2 rounded-full transition duration-200 ease-linear hover:bg-muted"
              >
                <ArrowLeftIcon />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{t('shop.product.sidebar.back.to.shops')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <h1 className="text-2xl font-semibold !mt-0">{shop.displayName}</h1>
      </CardHeader>

      <CardContent className="space-y-2">
        <div className="w-full">
          <Input
            className="rounded-full min-h-10"
            placeholder={t('label.search')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            clear
          />
        </div>

        {(selectedCategoryId !== null || selectedSubCategoryId !== null) && (
          <div className="pt-1">
            <Button
              variant="link"
              size="custom_sm"
              className="px-0"
              onClick={handleResetCategories}
            >
              <ArrowLeftIcon />
              {t('shop.product.sidebar.reset.categories')}
            </Button>
          </div>
        )}

        <div className="pt-2 space-y-2">
          <h3 className="text-sm font-semibold">{t('shop.filters.categories')}</h3>
          {productCategories.map((category) =>
            category.childs?.length ? (
              <Collapsible
                key={category.id}
                className="w-full"
                open={openCategoryIds.includes(category.id)}
                onOpenChange={(open) => setCategoryOpen(category.id, open)}
              >
                <div className="flex items-center w-full min-w-0">
                  <button
                    type="button"
                    onClick={() => handleSelectCategory(category.id)}
                    className={cn(
                      'flex-1 py-1 text-sm font-bold text-left rounded transition-colors hover:bg-muted/30 whitespace-nowrap overflow-hidden text-ellipsis',
                      selectedCategoryId === category.id ? 'text-primary' : ''
                    )}
                    title={category.label}
                  >
                    {category.label}
                  </button>
                  <CollapsibleTrigger className="p-1 rounded transition-colors hover:bg-muted/50">
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180 flex-shrink-0" />
                  </CollapsibleTrigger>
                </div>

                <CollapsibleContent>
                  <div className="pl-4 mt-2 space-y-1">
                    {category.childs.map((subCategory) => {
                      const isSelected = selectedSubCategoryId === subCategory.id;
                      return (
                        <button
                          type="button"
                          key={subCategory.id}
                          onClick={() => handleSelectSubCategory(subCategory.id)}
                          className={cn(
                            'overflow-hidden py-1 w-full text-sm text-left whitespace-nowrap rounded hover:bg-muted/30 text-ellipsis',
                            isSelected ? 'font-bold text-primary' : 'text-muted-foreground'
                          )}
                          title={subCategory.label}
                        >
                          {subCategory.label}
                        </button>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <button
                key={category.id}
                type="button"
                onClick={() => handleSelectCategory(category.id)}
                className={cn(
                  'w-full py-1 text-sm font-bold text-left rounded transition-colors hover:bg-muted/30 whitespace-nowrap overflow-hidden text-ellipsis',
                  selectedCategoryId === category.id ? 'text-primary' : ''
                )}
                title={category.label}
              >
                {category.label}
              </button>
            )
          )}
        </div>

        <ProductBrandFilters
          brands={brands}
          selectedBrandIds={selectedBrandIds}
          onToggleBrand={handleToggleBrand}
        />
      </CardContent>
    </Card>
  );
};

export { ShopProductsSidebar };
